import { translations } from '@/i18n';

export const LANG_KEYS = ['ar', 'en', 'ku'];

// Non-localized values (images, links, numbers, flags) merged into every language tree.
// Everything here becomes editable in the admin panel.
const VEHICLE_IMGS = [
  'https://images.unsplash.com/photo-1766561994067-dbd575e1cff2?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  'https://images.unsplash.com/photo-1623869675781-80aa31012a5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  'https://images.unsplash.com/photo-1605678141352-ccd8c79848d9?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  'https://images.unsplash.com/photo-1786545638192-2f2827f4d771?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  'https://images.unsplash.com/photo-1759665996018-4e966f640ee7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  'https://images.unsplash.com/photo-1752839971837-58515a01d879?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
];

export const SHARED_DEFAULTS = {
  settings: {
    siteName: 'WiyakGPS',
    logo: '/logo.jpeg',
    phone: '+964 750 000 0000',
    whatsapp: '9647500000000',
    email: 'hello@wiyakgps.com',
    hotline: '+964 750 000 0000',
    coords: '35.5950° N, 44.0270° E',
    showWhatsapp: true,
    socials: [
      { id: 'facebook', url: '#', visible: true },
      { id: 'instagram', url: '#', visible: true },
      { id: 'telegram', url: '#', visible: true },
      { id: 'youtube', url: '#', visible: true },
    ],
  },
  assistant: { enabled: true, name: 'WiyakGPS Assistant', instructions: '', position: 'end' },
  home: { showMarquee: true, showStats: true, showHow: true, showVehicles: true, showBento: true, showCoverage: true, showDashboard: true, showApps: true, showCta: true },
  hero: {
    truckImg: 'https://images.unsplash.com/photo-1779859447598-a844c0c0a010?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    speed: '104', cta1Link: '/contact', cta2Link: '#dashboard-section', chip: 'WIYAKGPS · GPS FLEET PLATFORM',
  },
  stats: [{ visible: true }, { visible: true }, { visible: true }, { visible: true }],
  how: { steps: [{ visible: true }, { visible: true }, { visible: true }, { visible: true }, { visible: true }] },
  vehicles: { items: VEHICLE_IMGS.map((img) => ({ img, visible: true })) },
  bento: { items: Array.from({ length: 6 }, () => ({ visible: true })) },
  coverage: { mapStyle: 'osm' },
  dashboard: { img: '/dashboard-preview.png', openLink: '/login', url: 'app.wiyakgps.com/dashboard' },
  apps: { platforms: Array.from({ length: 4 }, () => ({ visible: true, link: '/download' })), ctaLink: '/apps' },
  appsPage: { features: Array.from({ length: 6 }, () => ({ visible: true })) },
  ctaBand: { cta1Link: '/contact', cta3Link: '/download' },
  about: { img: '/logo.jpeg', badge: 'EST. ERBIL · IRAQ', why: { items: Array.from({ length: 4 }, () => ({ visible: true })) }, ctaLink: '/contact' },
  testimonials: { items: Array.from({ length: 3 }, () => ({ visible: true })) },
  solutions: { items: Array.from({ length: 7 }, () => ({ visible: true, link: '/contact' })) },
  servicesPage: { items: Array.from({ length: 10 }, () => ({ visible: true })) },
  offers: {
    cards: [
      { visible: true, pdf: '/pdf/wiyakgps-gps-offer.pdf', expires: '', requestLink: '/contact' },
      { visible: true, pdf: '/pdf/wiyakgps-fleet-offer.pdf', expires: '', requestLink: '/contact' },
      { visible: true, pdf: '/pdf/wiyakgps-projects-offer.pdf', expires: '', requestLink: '/contact' },
    ],
    showCalc: true, showPromo: true, calc: { pricePerVehicle: 14, fuelPerVehicle: 120, savingsPct: 28 },
  },
  maintenance: {
    img: 'https://images.unsplash.com/photo-1615906655593-ad0386982a0f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
    timeline: Array.from({ length: 4 }, () => ({ visible: true })),
    hardware: { items: Array.from({ length: 4 }, () => ({ visible: true })) },
  },
  support: {
    remote: { anydesk: 'https://anydesk.com/download', showAnydesk: true, showTeamviewer: true },
    videos: { items: [{ url: '', visible: true }, { url: '', visible: true }, { url: '', visible: true }] },
    showHotline: true, showRemote: true, showHours: true, showVideos: true,
  },
  faqPage: { items: Array.from({ length: 7 }, () => ({ visible: true })) },
  contact: { offices: { items: Array.from({ length: 3 }, () => ({ visible: true })) } },
  download: {
    links: { ios: '#', android: '#', apk: '#', win: '#', mac: '#', web: '#' },
    show: { ios: true, android: true, apk: true, win: true, mac: true, web: true },
    docs: { items: [{ visible: true }, { visible: true }] },
    showQr: true, showNotes: true, showReqs: true,
  },
};

const isObj = (v) => v && typeof v === 'object' && !Array.isArray(v);

// Defaults merge: arrays merged index-wise (so image/flag defaults attach to translated items).
export function mergeDefaults(base, extra) {
  if (Array.isArray(base) && Array.isArray(extra)) {
    const len = Math.max(base.length, extra.length);
    return Array.from({ length: len }, (_, i) => {
      if (i >= base.length) return extra[i];
      if (i >= extra.length) return base[i];
      return mergeDefaults(base[i], extra[i]);
    });
  }
  if (isObj(base) && isObj(extra)) {
    const out = { ...base };
    Object.keys(extra).forEach((k) => { out[k] = k in base ? mergeDefaults(base[k], extra[k]) : extra[k]; });
    return out;
  }
  return extra === undefined ? base : (isObj(extra) || Array.isArray(extra)) && typeof base === 'string' ? { t: base, ...extra } : extra;
}

// Published overlay: arrays in the overlay replace defaults entirely (deletion/reorder aware).
export function overlay(base, over) {
  if (over === undefined || over === null) return base;
  if (Array.isArray(over)) return over;
  if (isObj(base) && isObj(over)) {
    const out = { ...base };
    Object.keys(over).forEach((k) => { out[k] = overlay(base[k], over[k]); });
    return out;
  }
  return over;
}

export function buildBase() {
  const out = {};
  LANG_KEYS.forEach((l) => { out[l] = mergeDefaults(translations[l], SHARED_DEFAULTS); });
  return out;
}

export function getAt(obj, path) {
  return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

export function setAt(obj, path, value) {
  const keys = path.split('.');
  const root = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = root;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    const next = cur[k];
    cur[k] = Array.isArray(next) ? [...next] : isObj(next) ? { ...next } : (/^\d+$/.test(keys[i + 1]) ? [] : {});
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return root;
}

export const isExpired = (d) => !!d && new Date(d).getTime() < Date.now() - 86400000;

// Removes hidden/expired items from arrays so public components need no extra logic.
export function filterVisible(v) {
  if (Array.isArray(v)) return v.filter((x) => x !== '' && !(isObj(x) && (x.visible === false || isExpired(x.expires))));
  return v;
}
